import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fade,
  Paper,
  LinearProgress,
  Pagination,
  IconButton,
} from '@mui/material';
import {
  Search,
  RefreshCw,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../components/common/Notification';
import { certificatesService } from '../../services/certificates';
import CustomButton from '../../components/ui/CustomButton';

// Import the ViewCertificateDialog for viewing certificate details
import ViewCertificateDialog from './ViewCerificateDialog';
// Import the CertificateCard component
import CertificateCard from '../../components/common/CertificateCard';

const CertsWithoutAttachmentsPage = () => {
  const { isDarkMode } = useTheme();
  const { showNotification, NotificationComponent } = useNotification();

  const [allCertificates, setAllCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [displayedCertificates, setDisplayedCertificates] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20); // User can now change this
  const [isExporting, setIsExporting] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    drafts: 0,
    production: 0,
    thisMonth: 0,
  });

  // Fetch certificates without attachments from API
  const fetchCertificatesWithoutAttachments = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await certificatesService.getCertificatesWithoutAttachments({
        pageSize: 10000, // Get all certificates without attachments
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      // Handle different response structures
      let certificatesData = [];
      if (Array.isArray(response)) {
        certificatesData = response;
      } else if (response.data && Array.isArray(response.data)) {
        certificatesData = response.data;
      } else if (
        response.certificates &&
        Array.isArray(response.certificates)
      ) {
        certificatesData = response.certificates;
      }

      setAllCertificates(certificatesData);

      // Calculate statistics from loaded data
      calculateStats(certificatesData);
    } catch (err) {
      setError(err.message || 'Failed to fetch certificates without attachments');
      showNotification('Failed to load certificates without attachments', 'error');
      setAllCertificates([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate statistics from certificates data
  const calculateStats = certificates => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const stats = {
      total: certificates.length,
      drafts: certificates.filter(cert => cert.IsDraft === true).length,
      production: certificates.filter(cert => cert.IsDraft === false).length,
      thisMonth: certificates.filter(cert => {
        const createdDate = new Date(cert.CreatedAt);
        return createdDate >= thisMonthStart;
      }).length,
      expired: certificates.filter(cert => {
        if (cert.IsDraft) return false;
        if (!cert.ExpiryDate || cert.ExpiryDate === '0001-01-01T00:00:00')
          return false;
        return new Date(cert.ExpiryDate) < now;
      }).length,
    };

    stats.approved = stats.production;
    stats.pending = stats.drafts;

    setStats(stats);
  };

  // Enhanced search function
  const searchCertificates = (certificates, searchTerm) => {
    if (!searchTerm) return certificates;

    const term = searchTerm.toLowerCase();
    return certificates.filter(cert => {
      const searchableFields = [
        cert.CocNumber,
        cert.ImporterName,
        cert.ExporterName,
        cert.InvoiceNumber,
        cert.RegistrationNumber,
        cert.ReferenceNumber,
        cert.ImporterAddress,
        cert.ExporterCountry,
      ];

      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(term)
      );
    });
  };

  // Filter certificates by status
  const filterByStatus = (certificates, status) => {
    if (status === 'all') return certificates;
    if (status === 'draft') return certificates.filter(cert => cert.IsDraft === true);
    if (status === 'production') return certificates.filter(cert => cert.IsDraft === false);
    if (status === 'expired') {
      const now = new Date();
      return certificates.filter(cert => {
        if (cert.IsDraft) return false;
        if (!cert.ExpiryDate || cert.ExpiryDate === '0001-01-01T00:00:00') return false;
        return new Date(cert.ExpiryDate) < now;
      });
    }
    return certificates;
  };

  // Filter certificates by date
  const filterByDate = (certificates, dateFilter, startDate, endDate) => {
    if (dateFilter === 'all') return certificates;

    const now = new Date();
    let filterStartDate, filterEndDate;

    if (dateFilter === 'custom') {
      if (!startDate || !endDate) return certificates;
      filterStartDate = new Date(startDate);
      filterEndDate = new Date(endDate);
      filterEndDate.setHours(23, 59, 59, 999);
    } else if (dateFilter === 'today') {
      filterStartDate = new Date(now.setHours(0, 0, 0, 0));
      filterEndDate = new Date(now.setHours(23, 59, 59, 999));
    } else if (dateFilter === 'week') {
      filterStartDate = new Date(now.setDate(now.getDate() - 7));
      filterEndDate = new Date();
    } else if (dateFilter === 'month') {
      filterStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
      filterEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }

    return certificates.filter(cert => {
      const certDate = new Date(cert.CreatedAt);
      return certDate >= filterStartDate && certDate <= filterEndDate;
    });
  };

  // Apply all filters
  const applyFilters = () => {
    let filtered = [...allCertificates];

    // Apply search
    filtered = searchCertificates(filtered, debouncedSearchTerm);

    // Apply status filter
    filtered = filterByStatus(filtered, statusFilter);

    // Apply date filter
    filtered = filterByDate(filtered, dateFilter, startDate, endDate);

    setFilteredCertificates(filtered);
    setTotalCount(filtered.length);

    // Calculate pagination
    const pages = Math.ceil(filtered.length / pageSize);
    setTotalPages(pages);

    // Reset to first page if current page is beyond available pages
    if (currentPage > pages && pages > 0) {
      setCurrentPage(1);
    }
  };

  // Update displayed certificates for current page
  const updateDisplayedCertificates = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pageCertificates = filteredCertificates.slice(startIndex, endIndex);
    setDisplayedCertificates(pageCertificates);
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Initial data fetch
  useEffect(() => {
    fetchCertificatesWithoutAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCertificates, debouncedSearchTerm, statusFilter, dateFilter, startDate, endDate, pageSize]);

  // Update displayed certificates when filtered certificates or page changes
  useEffect(() => {
    updateDisplayedCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCertificates, currentPage, pageSize]);

  // Handle certificate view
  const handleViewCertificate = certificate => {
    setSelectedCertificate(certificate);
    setViewDialogOpen(true);
  };

  // Handle export to Excel using API
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);

      // Prepare filter parameters for API
      const exportParams = {
        ...(debouncedSearchTerm && { searchTerm: debouncedSearchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(dateFilter === 'custom' && startDate && endDate && {
          dateFrom: startDate,
          dateTo: endDate,
        }),
      };

      // Call API to get Excel file
      const blob = await certificatesService.exportCertificatesWithoutAttachmentsToExcel(
        exportParams
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const fileName = `certificates_without_attachments_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', fileName);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification(
        `Exported certificates without attachments to Excel`,
        'success'
      );
    } catch (error) {
      console.error('Export error:', error);
      showNotification(
        error?.message || 'Failed to export certificates to Excel',
        'error'
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Modern color theme
  const colors = {
    primary: '#3b82f6',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#06b6d4',
  };

  return (
    <Box sx={{ p: 3 }}>
      <NotificationComponent />
      
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AlertTriangle 
            size={28} 
            color={colors.warning}
            style={{ marginRight: '12px' }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${colors.warning} 0%, ${colors.error} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Certificates Without Attachments
          </Typography>
        </Box>
        
        <Typography
          variant="subtitle1"
          sx={{
            color: isDarkMode ? '#94a3b8' : '#64748b',
            fontWeight: 500,
          }}
        >
          View and manage certificates that are missing attachments
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.warning}15 0%, ${colors.warning}05 100%)`,
              border: `1px solid ${colors.warning}30`,
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ color: colors.warning, fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                Total Without Attachments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.info}15 0%, ${colors.info}05 100%)`,
              border: `1px solid ${colors.info}30`,
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ color: colors.info, fontWeight: 700 }}>
                {stats.drafts}
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                Draft Status
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.success}15 0%, ${colors.success}05 100%)`,
              border: `1px solid ${colors.success}30`,
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ color: colors.success, fontWeight: 700 }}>
                {stats.production}
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                Production Status
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.primary}05 100%)`,
              border: `1px solid ${colors.primary}30`,
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ color: colors.primary, fontWeight: 700 }}>
                {stats.thisMonth}
              </Typography>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                This Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.7) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.8) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
        }}
      >
        <Grid container spacing={3} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search certificates..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                },
              }}
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={e => setStatusFilter(e.target.value)}
                sx={{ borderRadius: 3 }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="production">Production</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                label="Date Range"
                onChange={e => setDateFilter(e.target.value)}
                sx={{ borderRadius: 3 }}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <CustomButton
                onClick={fetchCertificatesWithoutAttachments}
                disabled={isLoading}
                startIcon={<RefreshCw size={16} />}
                sx={{ borderRadius: 3 }}
              >
                Refresh
              </CustomButton>
              
              <CustomButton
                variant="outlined"
                onClick={handleExportToExcel}
                disabled={isExporting || filteredCertificates.length === 0}
                startIcon={<FileSpreadsheet size={16} />}
                sx={{ borderRadius: 3 }}
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </CustomButton>
            </Box>
          </Grid>
        </Grid>

        {/* Custom Date Range Inputs */}
        {dateFilter === 'custom' && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Results Summary */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography
            variant="body1"
            sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 500 }}
          >
            Showing {displayedCertificates.length} of {totalCount} certificates without attachments
          </Typography>
          
          {totalCount > 0 && (
            <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
              Page {currentPage} of {totalPages}
            </Typography>
          )}
        </Box>
        
        {/* Page Size Selector */}
        {totalCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              Per page:
            </Typography>
            <FormControl size="small">
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                sx={{
                  minWidth: 70,
                  height: 32,
                  borderRadius: 1.5,
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.5)'
                    : 'rgba(255, 255, 255, 0.5)',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: isDarkMode ? '#e2e8f0' : '#1e293b',
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            sx={{
              borderRadius: 2,
              height: 6,
              background: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.2)',
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
              },
            }}
          />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 3,
            '& .MuiAlert-message': { fontWeight: 500 },
          }}
        >
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && !error && totalCount === 0 && (
        <Card
          sx={{
            textAlign: 'center',
            py: 8,
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.5) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(248, 250, 252, 0.8) 100%)',
            backdropFilter: 'blur(20px)',
            border: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(148, 163, 184, 0.2)',
          }}
        >
          <CardContent>
            <AlertTriangle size={64} color={colors.success} style={{ marginBottom: '16px' }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Great News! No Certificates Without Attachments
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
              All certificates have proper attachments.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Certificates Grid */}
      {!isLoading && !error && displayedCertificates.length > 0 && (
        <Fade in>
          <Grid container spacing={3}>
            {displayedCertificates.map(certificate => (
              <Grid item xs={12} sm={6} lg={4} key={certificate.Id}>
                <CertificateCard
                  certificate={certificate}
                  onView={() => handleViewCertificate(certificate)}
                  showAttachmentWarning={true}
                />
              </Grid>
            ))}
          </Grid>
        </Fade>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Paper
            sx={{
              p: 2,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  fontWeight: 500,
                }}
              >
                Page {currentPage} of {totalPages}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  padding: '6px 10px',
                  borderRadius: 1.5,
                  border: isDarkMode
                    ? '1px solid rgba(59, 130, 246, 0.2)'
                    : '1px solid rgba(59, 130, 246, 0.1)',
                }}
              >
                <IconButton
                  onClick={(e) => handlePageChange(e, 1)}
                  disabled={currentPage === 1}
                  size="small"
                  sx={{
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                    },
                    '&:disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronsLeft size={16} />
                </IconButton>

                <IconButton
                  onClick={(e) => handlePageChange(e, Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  size="small"
                  sx={{
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                    },
                    '&:disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronLeft size={16} />
                </IconButton>

                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  hidePrevButton
                  hideNextButton
                  siblingCount={0}
                  boundaryCount={1}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: isDarkMode ? '#e2e8f0' : '#475569',
                      fontWeight: 600,
                      minWidth: 32,
                      height: 32,
                      fontSize: '0.8rem',
                      background: isDarkMode
                        ? 'rgba(30, 41, 59, 0.5)'
                        : 'rgba(255, 255, 255, 0.8)',
                      border: isDarkMode
                        ? '1px solid rgba(148, 163, 184, 0.2)'
                        : '1px solid rgba(148, 163, 184, 0.3)',
                      '&.Mui-selected': {
                        background:
                          'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        color: 'white',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                        fontWeight: 700,
                      },
                      '&:hover': {
                        background: 'rgba(59, 130, 246, 0.2)',
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                      },
                    },
                  }}
                />

                <IconButton
                  onClick={(e) => handlePageChange(e, Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  size="small"
                  sx={{
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                    },
                    '&:disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronRight size={16} />
                </IconButton>

                <IconButton
                  onClick={(e) => handlePageChange(e, totalPages)}
                  disabled={currentPage === totalPages}
                  size="small"
                  sx={{
                    color: isDarkMode ? '#e2e8f0' : '#475569',
                    background: isDarkMode
                      ? 'rgba(30, 41, 59, 0.5)'
                      : 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                    },
                    '&:disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronsRight size={16} />
                </IconButton>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? '#64748b' : '#94a3b8',
                }}
              >
                Showing {displayedCertificates.length} of {totalCount}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* View Certificate Dialog */}
      <ViewCertificateDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedCertificate(null);
        }}
        certificate={selectedCertificate}
        onCertificateUpdate={fetchCertificatesWithoutAttachments}
      />
    </Box>
  );
};

export default CertsWithoutAttachmentsPage;
